import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { SafeArea } from 'capacitor-plugin-safe-area';

export default function useSafeAreaInsets() {
    // Defaults
    const MIN_TOP = 20;
    const MIN_BOTTOM = 12;
    const MAX_BOTTOM = 20;
    const BOTTOM_OFFSET = 10;
    const BOTTOM_THRESHOLD = 25; // minimum to consider significant inset

    useEffect(() => {
        async function updateInsets() {
            try {
                let top;
                let right;
                let bottom;
                let left;

                if (Capacitor.isNativePlatform()) {
                    const { insets } = await SafeArea.getSafeAreaInsets();

                    top = insets?.top ?? 0;
                    right = insets?.right ?? 0;
                    left = insets?.left ?? 0;
                    bottom = insets?.bottom ?? 0;

                    // Detect if device has a soft navigation bar
                    const hasSoftNavBar =
                        window.screen && window.innerHeight
                        ? window.screen.height - window.innerHeight > 0
                        : false;

                    if (bottom > BOTTOM_THRESHOLD || hasSoftNavBar) {
                        top = insets?.top ?? MIN_TOP;
                        bottom = Math.min(Math.max(0, bottom - BOTTOM_OFFSET), MAX_BOTTOM);
                    } else {
                        bottom = 0;
                    }

                    // Apply CSS variables
                    document.documentElement.style.setProperty('--safe-area-top', `${top}px`);
                    document.documentElement.style.setProperty('--safe-area-right', `${right}px`);
                    document.documentElement.style.setProperty('--safe-area-bottom', `${bottom}px`);
                    document.documentElement.style.setProperty('--safe-area-left', `${left}px`);
                } else {
                    document.documentElement.style.setProperty('--safe-area-top', `env(safe-area-inset-top, ${MIN_TOP}px)`);
                    document.documentElement.style.setProperty('--safe-area-right', `env(safe-area-inset-right, 0px)`);
                    document.documentElement.style.setProperty('--safe-area-bottom', `env(safe-area-inset-bottom, ${MIN_BOTTOM}px)`);
                    document.documentElement.style.setProperty('--safe-area-left', `env(safe-area-inset-left, 0px)`);
                }
            } catch (err) {
                console.warn('Failed to get safe area insets', err);
            }
        }

        updateInsets();
        window.addEventListener('resize', updateInsets);
        return () => window.removeEventListener('resize', updateInsets);
    }, []);
}
